/**
 * Dashboard (Punkt 6): Kennzahlen, Verteilung und Entwicklung der Lizenzen.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  CardHeader,
  Dropdown,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Option,
  Subtitle2,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowDownloadRegular,
  ArrowSyncRegular,
  DismissRegular,
  CheckmarkCircleRegular,
  MoneyRegular,
  PersonProhibitedRegular,
  PlugConnectedRegular,
  KeyMultipleRegular,
  TagMultipleRegular,
} from '@fluentui/react-icons';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Link } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader.js';
import { ColumnFilterRow, useColumnFilters } from '../components/ColumnFilters.js';
import type { FilterColumn } from '../lib/columnFilter.js';
import { KpiCard } from '../components/KpiCard.js';
import { EmptyView, ErrorView, LoadingView } from '../components/StateViews.js';
import { NumericHeaderCell } from '../components/NumericHeaderCell.js';
import { useSession } from '../lib/session.js';
import { useApi } from '../lib/useApi.js';
import { useReportDefaults } from '../lib/useReportDefaults.js';
import { useI18n } from '../i18n/index.js';
import { api } from '../api/endpoints.js';
import { PERMISSIONS, type SkuSummary } from '../api/types.js';
import { SEMANTIC_COLORS, colorForIndex } from '../lib/chartColors.js';

const useStyles = makeStyles({
  kpis: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalXL },
  charts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalXL,
  },
  card: { padding: tokens.spacingVerticalM },
  chartBox: { width: '100%', height: '300px' },
  tableCard: { padding: tokens.spacingVerticalM },
  productLink: { color: 'inherit', textDecorationLine: 'none', ':hover': { textDecorationLine: 'underline' } },
  numeric: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  controls: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center', flexWrap: 'wrap' },
  muted: { color: tokens.colorNeutralForeground3 },
  notices: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS, marginBottom: tokens.spacingVerticalM },
});

const TREND_RANGES = [30, 90, 180, 365] as const;

/**
 * Einschraenkung der Produkttabelle, ausgeloest durch eine Kennzahlenkachel.
 *
 * Kacheln, deren Detail eine Person ist (vergeben, deaktivierte Konten),
 * verweisen auf die Zuweisungsseite. Kacheln, deren Detail ein Produkt ist,
 * schraenken stattdessen die Tabelle auf dieser Seite ein – ein Seitenwechsel
 * waere hier ein Umweg, die Antwort steht schon darunter.
 */
type ProductFocus =
  | { kind: 'available' }
  | { kind: 'lowUtilization' }
  | { kind: 'shared' }
  | { kind: 'unpriced' }
  | { kind: 'category'; category: string | null }
  | { kind: 'provider'; providerId: string; name: string }
  | null;

export function DashboardPage() {
  const styles = useStyles();
  const { client, has } = useSession();
  const { t, formatNumber, formatPercent, formatDateTime, formatCurrency } = useI18n();
  const [trendDays, setTrendDays] = useState<number>(90);
  const [exporting, setExporting] = useState(false);
  const [focus, setFocus] = useState<ProductFocus>(null);
  const productTableRef = useRef<HTMLDivElement | null>(null);

  /** Setzt den Fokus und bringt die Tabelle ins Bild – ohne Scrollen bliebe der Klick unbemerkt. */
  const focusProducts = useCallback((next: ProductFocus) => {
    setFocus(next);
    // Erst nach dem Rendern scrollen, sonst springt die Seite an die alte Hoehe.
    window.requestAnimationFrame(() => productTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, []);
  const { includeFree, includeDisabled, excludeFree, excludeDisabled, setIncludeFree, setIncludeDisabled } = useReportDefaults();

  const dashboard = useApi((signal) => api.dashboard(client, { excludeFree, excludeDisabled }, signal), [client, excludeFree, excludeDisabled]);
  // Verwaiste Lizenzen: eigener Abruf, damit weder Antwortzeit noch Fehler
  // dieser Auswertung das Dashboard mitnehmen.
  const orphans = useApi((signal) => api.orphans(client, { excludeFree: excludeFree || undefined }, signal), [client, excludeFree]);
  const trend = useApi(
    (signal) => api.trend(client, { days: trendDays, excludeFree, excludeDisabled }, signal),
    [client, trendDays, excludeFree, excludeDisabled],
  );

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      await api.exportCsv(client, 'skus', { excludeFree, excludeDisabled });
    } finally {
      setExporting(false);
    }
  }, [client, excludeFree, excludeDisabled]);

  // Spaltenfilter der Produkttabelle. Steht VOR den fruehen Ruecksprüngen:
  // Ein Hook muss in jedem Renderdurchlauf aufgerufen werden, auch waehrend
  // die Daten noch laden.
  const kostenSichtbar = (dashboard.data?.costs.pricedSkuCount ?? 0) > 0;
  const productFilterColumns = useMemo<FilterColumn<SkuSummary>[]>(() => {
    const spalten: FilterColumn<SkuSummary>[] = [
      { key: 'product', label: t('dashboard.table.product'), get: (r) => r.displayName },
      { key: 'source', label: t('dashboard.table.source'), get: (r) => r.providerSlug },
      { key: 'total', label: t('dashboard.table.total'), get: (r) => r.totalUnits },
      { key: 'assigned', label: t('dashboard.table.assigned'), get: (r) => r.assignedUnits },
      { key: 'available', label: t('dashboard.table.available'), get: (r) => r.availableUnits },
      { key: 'disabled', label: t('dashboard.table.disabled'), get: (r) => r.assignedToDisabled },
      { key: 'inactive', label: t('dashboard.table.inactive'), get: (r) => r.assignedInactive },
      { key: 'utilization', label: t('dashboard.table.utilization'), get: (r) => r.utilizationPercent },
    ];
    if (kostenSichtbar) {
      spalten.push({ key: 'unitCost', label: t('dashboard.table.unitCost'), get: (r) => r.unitCost });
      spalten.push({ key: 'cost', label: t('dashboard.table.cost'), get: (r) => r.monthlyCostAssigned });
    }
    return spalten;
  }, [t, kostenSichtbar]);
  const productFilters = useColumnFilters(productFilterColumns);

  if (dashboard.loading) return <LoadingView />;
  if (dashboard.error) return <ErrorView error={dashboard.error} onRetry={dashboard.reload} />;

  const data = dashboard.data;
  if (!data || data.skus.length === 0) {
    return (
      <>
        <PageHeader title={t('dashboard.title')} />
        <EmptyView />
      </>
    );
  }

  const { totals, costs } = data;
  // Die Tabelle zeigt, was die angeklickte Kachel meint – dieselbe Grundmenge,
  // nur eingeschraenkt. Ohne Fokus bleibt sie vollstaendig.
  const fokusSkus = data.skus.filter((sku) => {
    if (focus === null) return true;
    switch (focus.kind) {
      case 'available':
        return sku.availableUnits > 0;
      case 'lowUtilization':
        // Gemeinkosten liegen bei 100 % und fallen damit von selbst heraus.
        return sku.totalUnits > 0 && sku.utilizationPercent < 80;
      case 'shared':
        return sku.allocation === 'shared';
      case 'unpriced':
        return !sku.isFree && sku.unitCost === null;
      case 'category':
        return (sku.category ?? null) === focus.category;
      case 'provider':
        return sku.providerId === focus.providerId;
    }
  });
  // Fokus (angeklickte Kachel) und Spaltenfilter wirken nacheinander, nicht
  // gegeneinander: erst die Kachel, dann die Eingaben in der Filterzeile.
  const visibleSkus = productFilters.apply(fokusSkus);
  const focusLabel = (f: Exclude<ProductFocus, null>): string => {
    switch (f.kind) {
      case 'available':
        return t('dashboard.focus.available');
      case 'lowUtilization':
        return t('dashboard.focus.lowUtilization');
      case 'shared':
        return t('dashboard.focus.shared');
      case 'unpriced':
        return t('dashboard.focus.unpriced');
      case 'category':
        return f.category ? t(`category.${f.category}` as 'category.other') : t('category.none');
      case 'provider':
        return f.name;
    }
  };
  // Kostenkacheln erst, wenn mindestens ein Produkt bepreist oder als kostenlos markiert ist.
  const showCosts = costs.pricedSkuCount > 0;
  // Fuer das Balkendiagramm nur die groessten Produkte zeigen; sonst wird es unleserlich.
  const topSkus = data.skus.slice(0, 12);
  const distribution = [
    { name: t('dashboard.assignedUnits'), value: totals.assignedUnits, color: SEMANTIC_COLORS.assigned },
    { name: t('dashboard.availableUnits'), value: totals.availableUnits, color: SEMANTIC_COLORS.available },
  ];

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        meta={`${t('common.lastUpdate')}: ${formatDateTime(data.capturedAt)}`}
        actions={
          <>
            <Switch label={t('dashboard.includeFree')} checked={includeFree} onChange={(_, d) => setIncludeFree(d.checked)} />
            <Switch
              label={t('dashboard.includeDisabled')}
              checked={includeDisabled}
              onChange={(_, d) => setIncludeDisabled(d.checked)}
            />
            <Button appearance="subtle" icon={<ArrowSyncRegular />} onClick={dashboard.reload}>
              {t('common.refresh')}
            </Button>
            {has(PERMISSIONS.ReportExport) ? (
              <Button
                appearance="primary"
                icon={<ArrowDownloadRegular />}
                disabled={exporting}
                onClick={() => void exportCsv()}
              >
                {t('common.exportCsv')}
              </Button>
            ) : null}
          </>
        }
      />

      <div className={styles.notices}>
        {excludeFree && totals.hiddenFreeSkuCount > 0 ? (
          <Caption1 className={styles.muted}>{t('dashboard.hiddenFree', { count: totals.hiddenFreeSkuCount })}</Caption1>
        ) : null}
        {excludeDisabled && totals.excludedDisabledUnits > 0 ? (
          <Caption1 className={styles.muted}>
            {t('dashboard.excludedDisabled', { count: formatNumber(totals.excludedDisabledUnits) })}
          </Caption1>
        ) : null}
        {showCosts && costs.unpricedSkuCount > 0 ? (
          <MessageBar intent="warning">
            <MessageBarBody>{t('dashboard.unpriced', { count: costs.unpricedSkuCount })}</MessageBarBody>
            <MessageBarActions>
              <Button size="small" appearance="transparent" onClick={() => focusProducts({ kind: 'unpriced' })}>
                {t('dashboard.action.showUnpriced')}
              </Button>
            </MessageBarActions>
          </MessageBar>
        ) : null}
        {costs.mixedCurrencies ? (
          <MessageBar intent="warning">
            <MessageBarBody>{t('dashboard.mixedCurrencies')}</MessageBarBody>
          </MessageBar>
        ) : null}
        {totals.sharedSkuCount > 0 ? (
          <Caption1 className={styles.muted}>{t('dashboard.sharedSkus', { count: totals.sharedSkuCount })}</Caption1>
        ) : null}
        {!showCosts && has(PERMISSIONS.PriceWrite) ? (
          <MessageBar intent="info">
            <MessageBarBody>{t('dashboard.noPrices')}</MessageBarBody>
          </MessageBar>
        ) : null}
      </div>

      <section className={styles.kpis} aria-label={t('dashboard.title')}>
        <KpiCard
          label={t('dashboard.totalUnits')}
          value={formatNumber(totals.totalUnits)}
          hint={t('dashboard.totalUnitsHint')}
          icon={<TagMultipleRegular />}
          actionHint={t('dashboard.action.allProducts')}
          onClick={() => focusProducts(null)}
        />
        <KpiCard
          label={t('dashboard.assignedUnits')}
          value={formatNumber(totals.assignedUnits)}
          hint={t('dashboard.assignedUnitsHint')}
          icon={<KeyMultipleRegular />}
          actionHint={t('dashboard.action.assignments')}
          to="/zuweisungen?status=active"
        />
        <KpiCard
          label={t('dashboard.availableUnits')}
          value={formatNumber(totals.availableUnits)}
          hint={t('dashboard.availableUnitsHint')}
          icon={<CheckmarkCircleRegular />}
          accent="success"
          actionHint={t('dashboard.action.available')}
          onClick={() => focusProducts({ kind: 'available' })}
        />
        <KpiCard
          label={t('dashboard.unusedUnits')}
          value={formatNumber(totals.assignedToDisabled)}
          hint={t('dashboard.unusedUnitsHint')}
          icon={<PersonProhibitedRegular />}
          accent="warning"
          actionHint={t('dashboard.action.disabled')}
          to="/zuweisungen?status=disabled"
        />
        {/* Verwaiste Lizenzen anderer Anbieter. Eigener Abruf, nicht Teil der
            Dashboard-Antwort: Die Auswertung vergleicht jede Zuweisung mit dem
            gesamten Verzeichnis. Schlaegt sie fehl oder gibt es kein
            Verzeichnis, bleibt die Kachel weg - eine Null waere dort eine
            Entwarnung, die niemand geprueft hat. */}
        {orphans.data?.directoryAvailable && orphans.data.totals.actionableCount > 0 ? (
          <KpiCard
            label={t('dashboard.orphans')}
            value={formatNumber(orphans.data.totals.actionableCount)}
            hint={t('dashboard.orphansHint')}
            icon={<PersonProhibitedRegular />}
            accent="warning"
            actionHint={t('dashboard.action.orphans')}
            to="/verwaiste-lizenzen"
          />
        ) : null}
        <KpiCard
          label={t('dashboard.utilization')}
          value={formatPercent(totals.utilizationPercent)}
          actionHint={t('dashboard.action.lowUtilization')}
          onClick={() => focusProducts({ kind: 'lowUtilization' })}
        />
      </section>

      {showCosts ? (
        <section className={styles.kpis} aria-label={t('dashboard.costAssigned')}>
          <KpiCard
            label={t('dashboard.costAssigned')}
            value={formatCurrency(costs.monthlyCostAssigned, costs.currency)}
            hint={t('dashboard.costAssignedHint')}
            icon={<MoneyRegular />}
            actionHint={t('dashboard.action.people')}
            to="/personen?onlyPriced=true"
          />
          <KpiCard
            label={t('dashboard.costAvailable')}
            value={formatCurrency(costs.monthlyCostAvailable, costs.currency)}
            hint={t('dashboard.costAvailableHint')}
            icon={<MoneyRegular />}
            accent="warning"
            actionHint={t('dashboard.action.available')}
            onClick={() => focusProducts({ kind: 'available' })}
          />
          <KpiCard
            label={t('dashboard.costUnused')}
            value={formatCurrency(costs.monthlyCostUnused, costs.currency)}
            hint={t('dashboard.costUnusedHint')}
            icon={<MoneyRegular />}
            accent="warning"
            actionHint={t('dashboard.action.disabled')}
            to="/zuweisungen?status=disabled"
          />
          {costs.monthlyCostShared > 0 ? (
            <>
              <KpiCard
                label={t('dashboard.costShared')}
                value={formatCurrency(costs.monthlyCostShared, costs.currency)}
                hint={t('dashboard.costSharedHint')}
                icon={<MoneyRegular />}
                actionHint={t('dashboard.action.shared')}
                onClick={() => focusProducts({ kind: 'shared' })}
              />
              <KpiCard
                label={t('dashboard.costTotal')}
                value={formatCurrency(costs.monthlyCostTotal, costs.currency)}
                hint={t('dashboard.costTotalHint')}
                icon={<MoneyRegular />}
                actionHint={t('dashboard.action.countries')}
                to="/laender"
              />
            </>
          ) : null}
        </section>
      ) : null}

      {showCosts && costs.byCategory.length > 0 ? (
        <Card className={styles.card}>
          <CardHeader header={<Subtitle2>{t('dashboard.costByCategory')}</Subtitle2>} />
          <div className="table-scroll narrow">
            <Table size="small" aria-label={t('dashboard.costByCategory')}>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>{t('dashboard.table.category')}</TableHeaderCell>
                  <NumericHeaderCell>{t('dashboard.table.products')}</NumericHeaderCell>
                  <NumericHeaderCell>{t('dashboard.table.cost')}</NumericHeaderCell>
                  <NumericHeaderCell>{t('dashboard.table.share')}</NumericHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costs.byCategory.map((c) => (
                  <TableRow key={c.category ?? '__none__'}>
                    <TableCell>
                      <Button
                        appearance="transparent"
                        size="small"
                        onClick={() => focusProducts({ kind: 'category', category: c.category })}
                        title={t('dashboard.action.category')}
                      >
                        {c.category ? t(`category.${c.category}` as 'category.other') : t('category.none')}
                      </Button>
                    </TableCell>
                    <TableCell className={styles.numeric}>
                      {formatNumber(c.skuCount)}
                      {c.unpricedSkuCount > 0 ? (
                        <Caption1 className={styles.muted}> · {t('countries.unpricedShort', { count: c.unpricedSkuCount })}</Caption1>
                      ) : null}
                    </TableCell>
                    <TableCell className={styles.numeric}>{formatCurrency(c.monthlyCost, costs.currency)}</TableCell>
                    <TableCell className={styles.numeric}>
                      {costs.monthlyCostTotal > 0 ? formatPercent(Math.round((c.monthlyCost / costs.monthlyCostTotal) * 1000) / 10) : '–'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : null}

      {data.providers.length > 1 ? (
        <section className={styles.kpis} aria-label={t('dashboard.bySource')}>
          {data.providers.map((provider) => (
            <KpiCard
              key={provider.id}
              label={provider.displayName}
              value={formatNumber(provider.totalUnits)}
              hint={
                provider.monthlyCost === null
                  ? t('dashboard.source.hint', { assigned: formatNumber(provider.assignedUnits) })
                  : t('dashboard.source.hintCost', {
                      assigned: formatNumber(provider.assignedUnits),
                      cost: formatCurrency(provider.monthlyCost, provider.currency ?? costs.currency),
                    })
              }
              icon={<PlugConnectedRegular />}
              accent={provider.enabled && provider.lastSyncStatus !== 'FAILED' ? 'default' : 'warning'}
              actionHint={t('dashboard.action.source')}
              onClick={() => focusProducts({ kind: 'provider', providerId: provider.id, name: provider.displayName })}
            />
          ))}
        </section>
      ) : null}

      <section className={styles.charts}>
        <Card className={styles.card}>
          <CardHeader header={<Subtitle2>{t('dashboard.byProduct')}</Subtitle2>} />
          <div className={styles.chartBox}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topSkus} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis dataKey="displayName" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" height={90} />
                <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
                <Bar dataKey="assignedUnits" name={t('dashboard.assignedUnits')} fill={SEMANTIC_COLORS.assigned} stackId="a" />
                <Bar dataKey="availableUnits" name={t('dashboard.availableUnits')} fill={SEMANTIC_COLORS.available} stackId="a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className={styles.card}>
          <CardHeader header={<Subtitle2>{t('dashboard.distribution')}</Subtitle2>} />
          <div className={styles.chartBox}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={distribution}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="55%"
                  outerRadius="80%"
                  paddingAngle={2}
                  label={(entry: { name: string; value: number }) => `${entry.name}: ${formatNumber(entry.value)}`}
                >
                  {distribution.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatNumber(value)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className={styles.card}>
          <CardHeader
            header={<Subtitle2>{t('dashboard.trend')}</Subtitle2>}
            action={
              <div className={styles.controls}>
                <Dropdown
                  size="small"
                  aria-label={t('dashboard.trendDays')}
                  value={`${trendDays} Tage`}
                  selectedOptions={[String(trendDays)]}
                  onOptionSelect={(_, d) => setTrendDays(Number(d.optionValue))}
                >
                  {TREND_RANGES.map((days) => (
                    <Option key={days} value={String(days)}>
                      {`${days} Tage`}
                    </Option>
                  ))}
                </Dropdown>
              </div>
            }
          />
          <div className={styles.chartBox}>
            {trend.loading ? (
              <LoadingView />
            ) : (trend.data?.points.length ?? 0) < 2 ? (
              <EmptyView title={t('common.noData')} hint={t('dashboard.trend')} />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trend.data?.points ?? []} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis
                    dataKey="capturedAt"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value: string) => new Date(value).toLocaleDateString()}
                  />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <Tooltip
                    formatter={(value: number) => formatNumber(value)}
                    labelFormatter={(value: string) => formatDateTime(value)}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="totalUnits"
                    name={t('dashboard.totalUnits')}
                    stroke={colorForIndex(3)}
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="assignedUnits"
                    name={t('dashboard.assignedUnits')}
                    stroke={SEMANTIC_COLORS.assigned}
                    dot={false}
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="availableUnits"
                    name={t('dashboard.availableUnits')}
                    stroke={SEMANTIC_COLORS.available}
                    dot={false}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
      </section>

      <Card className={styles.tableCard} ref={productTableRef}>
        <CardHeader
          header={<Subtitle2>{t('dashboard.byProduct')}</Subtitle2>}
          action={
            focus !== null ? (
              <Button size="small" appearance="subtle" icon={<DismissRegular />} onClick={() => setFocus(null)}>
                {t('dashboard.focus.reset', { filter: focusLabel(focus) })}
              </Button>
            ) : undefined
          }
        />
        <div className="table-scroll wide">
          <Table aria-label={t('dashboard.byProduct')} size="small">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{t('dashboard.table.product')}</TableHeaderCell>
                <TableHeaderCell>{t('dashboard.table.source')}</TableHeaderCell>
                <NumericHeaderCell>{t('dashboard.table.total')}</NumericHeaderCell>
                <NumericHeaderCell>{t('dashboard.table.assigned')}</NumericHeaderCell>
                <NumericHeaderCell>{t('dashboard.table.available')}</NumericHeaderCell>
                <NumericHeaderCell>{t('dashboard.table.disabled')}</NumericHeaderCell>
                <NumericHeaderCell>{t('dashboard.table.inactive')}</NumericHeaderCell>
                <NumericHeaderCell>{t('dashboard.table.utilization')}</NumericHeaderCell>
                {showCosts ? (
                  <>
                    <NumericHeaderCell>{t('dashboard.table.unitCost')}</NumericHeaderCell>
                    <NumericHeaderCell>{t('dashboard.table.cost')}</NumericHeaderCell>
                  </>
                ) : null}
              </TableRow>
              <ColumnFilterRow state={productFilters} />
            </TableHeader>
            <TableBody>
              {visibleSkus.length === 0 ? (
                <TableRow>
                  {/* Ein leeres Ergebnis ohne Erklaerung liest sich wie ein Fehler. */}
                  <TableCell colSpan={productFilterColumns.length}>
                    <Caption1 className={styles.muted}>{t('dashboard.focus.empty')}</Caption1>
                  </TableCell>
                </TableRow>
              ) : null}
              {visibleSkus.map((sku) => (
                <TableRow key={sku.skuId}>
                  <TableCell>
                    {/* Der Produktname fuehrt zu den Zuweisungen dieses Produkts –
                        die naechste Frage nach "wie viele" ist immer "bei wem". */}
                    <Link
                      to={`/zuweisungen?product=${encodeURIComponent(sku.partNumber)}`}
                      className={styles.productLink}
                      title={t('dashboard.action.product')}
                    >
                      <Body1>{sku.displayName}</Body1>
                    </Link>{' '}
                    {sku.isFree ? (
                      <Badge appearance="tint" color="subtle" size="small">
                        {t('dashboard.table.free')}
                      </Badge>
                    ) : null}
                    {sku.allocation === 'shared' ? (
                      <Badge appearance="tint" color="brand" size="small">
                        {t('dashboard.sharedBadge')}
                      </Badge>
                    ) : null}
                  </TableCell>
                  <TableCell>{sku.providerSlug}</TableCell>
                  <TableCell className={styles.numeric}>{formatNumber(sku.totalUnits)}</TableCell>
                  <TableCell className={styles.numeric}>{formatNumber(sku.assignedUnits)}</TableCell>
                  <TableCell className={styles.numeric}>{formatNumber(sku.availableUnits)}</TableCell>
                  <TableCell className={styles.numeric}>{formatNumber(sku.assignedToDisabled)}</TableCell>
                  <TableCell className={styles.numeric}>{formatNumber(sku.assignedInactive)}</TableCell>
                  <TableCell className={styles.numeric}>{formatPercent(sku.utilizationPercent)}</TableCell>
                  {showCosts ? (
                    <>
                      <TableCell className={styles.numeric}>
                        {sku.isFree ? '–' : sku.unitCost === null ? (
                          <Caption1 className={styles.muted}>{t('dashboard.table.noPrice')}</Caption1>
                        ) : (
                          formatCurrency(sku.unitCost, sku.currency)
                        )}
                      </TableCell>
                      <TableCell className={styles.numeric}>
                        {sku.monthlyCostAssigned === null ? '–' : formatCurrency(sku.monthlyCostAssigned, sku.currency ?? costs.currency)}
                      </TableCell>
                    </>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </>
  );
}
